import styled, { keyframes, css } from 'styled-components';

/* ===== Identidade visual (alinhada ao login / Header) =====
   --primary:  #ff6d3c   --blue: #1789dd
   --ink:      #17263b   --muted: #64748b
   --border:   rgba(216, 224, 235, 0.9)
   success: #16a34a / #dcfce7 / #166534
   warning: #f59e0b / #fef3c7 / #92400e
   danger:  #dc2626 / #fee2e2 / #991b1b
=========================================================== */

const ACCENT = {
  default: '#1789dd',
  success: '#16a34a',
  warning: '#f59e0b',
  danger: '#dc2626',
};

const STATUS = {
  success: { bg: '#dcfce7', fg: '#166534' },
  warning: { bg: '#fef3c7', fg: '#92400e' },
  danger: { bg: '#fee2e2', fg: '#991b1b' },
};

const overlayIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const modalIn = keyframes`
  from { opacity: 0; transform: translateY(18px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5vh 2.5vw;

  background: rgba(6, 14, 26, 0.72);
  backdrop-filter: blur(6px);
  animation: ${overlayIn} 0.25s ease both;
`;

export const Modal = styled.div`
  display: flex;
  flex-direction: column;
  width: 95vw;
  height: 95vh;
  overflow: hidden;

  background: #f4f7fb;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 22px;
  box-shadow: 0 40px 110px rgba(6, 14, 26, 0.55),
    0 1px 0 rgba(255, 255, 255, 0.6) inset;
  animation: ${modalIn} 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
`;

export const ModalHeader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 26px;
  overflow: hidden;

  background: linear-gradient(120deg, #0a1729 0%, #0f2138 55%, #0a1729 100%);
  color: rgba(255, 255, 255, 0.78);

  /* brilho radial laranja, como nas telas de login */
  &::before {
    content: '';
    position: absolute;
    top: -60%;
    right: -4%;
    width: 320px;
    height: 320px;
    border-radius: 999px;
    background: radial-gradient(
      circle,
      rgba(255, 109, 60, 0.22),
      transparent 66%
    );
    pointer-events: none;
  }

  /* linha de acento na base */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      rgba(255, 109, 60, 0.9) 0%,
      rgba(255, 109, 60, 0.5) 32%,
      rgba(23, 137, 221, 0.65) 70%,
      transparent 100%
    );
  }

  > div {
    position: relative;
    z-index: 1;
  }

  h2 {
    margin: 0 0 0 8px;
    font-size: 22px;
    line-height: 1.1;
    letter-spacing: -0.4px;
    color: #fff;
  }

  span {
    display: block;
    margin-top: 6px;
    max-width: 720px;
    font-size: 13px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.66);
  }
`;

export const HeaderTitle = styled.div`
  display: flex;
  align-items: center;

  svg {
    color: #ff6d3c;
    filter: drop-shadow(0 6px 16px rgba(255, 109, 60, 0.45));
  }
`;

export const CloseButton = styled.button`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;

  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.07);
  color: #fff;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease,
    color 0.2s ease;

  &:hover {
    background: rgba(255, 109, 60, 0.18);
    border-color: rgba(255, 109, 60, 0.5);
    color: #ffd4c6;
    transform: rotate(90deg);
  }
`;

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: end;
  gap: 14px;
  padding: 16px 26px;
  background: #fff;
  border-bottom: 1px solid rgba(216, 224, 235, 0.9);

  label {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #4a5b72;
  }

  input,
  select {
    width: 160px;
    height: 40px;
    padding: 0 12px;
    border: 1px solid rgba(216, 224, 235, 0.95);
    border-radius: 12px;
    background: #f8fafc;
    color: #1f2f46;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease,
      background 0.2s ease;

    &:hover:not(:focus) {
      border-color: rgba(255, 109, 60, 0.4);
      background: #fff;
    }

    &:focus {
      border-color: #ff6d3c;
      background: #fff;
      box-shadow: 0 0 0 4px rgba(255, 109, 60, 0.12);
    }
  }

  /* botão "Atualizar" */
  > button {
    height: 40px;
    padding: 0 22px;
    border: 0;
    border-radius: 12px;
    color: #fff;
    font-size: 14px;
    font-weight: 800;
    cursor: pointer;

    background: linear-gradient(135deg, #ff6d3c 0%, #ff875f 52%, #ff9b78 100%);
    box-shadow: 0 12px 26px rgba(255, 109, 60, 0.32);
    transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 16px 32px rgba(255, 109, 60, 0.4);
      filter: brightness(1.02);
    }

    &:active {
      transform: translateY(0);
    }
  }
`;

export const ToolbarInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  min-width: 180px;
  padding: 0 18px;
  border-radius: 12px;
  border: 1px solid rgba(216, 224, 235, 0.9);
  background: rgba(247, 250, 253, 0.8);

  span {
    font-size: 12px;
    color: #64748b;
  }

  strong {
    font-size: 16px;
    color: #17263b;
  }
`;

export const ToolbarSpacer = styled.div`
  flex: 1;
`;

export const AutomationStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  min-width: 190px;
  padding: 0 16px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;

  ${({ variant }) => {
    const tone = STATUS[variant] || STATUS.warning;
    return css`
      background: ${tone.bg};
      color: ${tone.fg};
      box-shadow: inset 0 0 0 1px ${tone.fg}1f;
    `;
  }}

  span {
    opacity: 0.85;
  }

  strong {
    font-size: 13px;
  }

  small {
    font-size: 11px;
    opacity: 0.85;
  }
`;

export const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
`;

export const Loading = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 28px;
  font-size: 14px;
  font-weight: 700;
  color: #4a5b72;

  &::before {
    content: '';
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 3px solid rgba(255, 109, 60, 0.25);
    border-top-color: #ff6d3c;
    animation: ${spin} 0.8s linear infinite;
  }
`;

export const CardsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
`;

export const CardsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 14px;
`;

export const Card = styled.div`
  position: relative;
  width: 250px;
  padding: 18px;
  overflow: hidden;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.1),
    0 1px 0 rgba(255, 255, 255, 0.9) inset;
  transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.22s ease;

  /* barra de acento lateral arredondada (substitui os border-left/bottom) */
  &::before {
    content: '';
    position: absolute;
    top: 14px;
    bottom: 14px;
    left: 0;
    width: 5px;
    border-radius: 0 6px 6px 0;
    background: ${({ variant }) => ACCENT[variant] || ACCENT.default};
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16),
      0 1px 0 rgba(255, 255, 255, 0.9) inset;
  }

  span {
    display: block;
    margin-bottom: 8px;
    font-size: 12px;
    color: #64748b;
  }

  strong {
    font-size: 26px;
    line-height: 1;
    color: #17263b;
  }

  small {
    margin-left: 8px;
    font-size: 13px;
    font-weight: 700;
    color: ${({ variant }) => ACCENT[variant] || ACCENT.default};
  }
`;

export const CardInfo = styled.div`
  padding: 16px 18px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.08),
    0 1px 0 rgba(255, 255, 255, 0.9) inset;
  transition: transform 0.22s ease, box-shadow 0.22s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 30px rgba(15, 23, 42, 0.12);
  }

  span {
    display: block;
    margin-bottom: 6px;
    font-size: 12px;
    color: #64748b;
  }

  strong {
    font-size: 15px;
    color: #17263b;
  }

  small {
    display: block;
    margin-top: 4px;
    font-size: 12px;
    color: #64748b;
  }
`;

export const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  flex: 1;
  min-height: 0;
  padding: 0 24px 24px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const Panel = styled.section`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
  border: 1px solid rgba(216, 224, 235, 0.7);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.08);
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid rgba(216, 224, 235, 0.9);
  background: linear-gradient(180deg, #ffffff, #f7fafd);

  h3 {
    margin: 0;
    font-size: 16px;
    letter-spacing: -0.2px;
    color: #17263b;
  }

  span {
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(255, 109, 60, 0.1);
    color: #d4572a;
    font-size: 11px;
    font-weight: 800;
  }
`;

export const TableWrapper = styled.div`
  flex: 1;
  min-height: 260px;
  overflow: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }

  th,
  td {
    padding: 11px 12px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.9);
    text-align: left;
    white-space: nowrap;
    color: #3a4d66;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f1f5f9;
    color: #334155;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }

  tbody tr {
    transition: background 0.15s ease;

    &:hover {
      background: rgba(255, 109, 60, 0.05);
    }
  }
`;

export const Badge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;

  ${({ variant }) => {
    const tone = STATUS[variant] || STATUS.success;
    return css`
      background: ${tone.bg};
      color: ${tone.fg};
    `;
  }}
`;
